import { Controller } from "@hotwired/stimulus"

const SELECTED_CLASS = "payment-method-card--selected"

export default class extends Controller {
  static targets = ["subscribeButton", "hiddenInput"]
  static values = { 
    insurancePolicyId: String,
    selectedPaymentMethodId: String,
    hasExistingPaymentMethods: Boolean
  }

  connect() {
    this.setupPaymentComponent()
  }

  // Helper method to get CSRF token with fallbacks
  getCSRFToken() {
    // Try to get CSRF token from meta tag first
    const csrfMeta = document.querySelector('[name="csrf-token"]')
    if (csrfMeta && csrfMeta.content) {
      return csrfMeta.content
    }
    
    // Fallback: try to get from Rails.csrfToken() if available
    if (typeof Rails !== 'undefined' && Rails.csrfToken) {
      return Rails.csrfToken()
    }
    
    // Fallback: try to get from form authenticity token
    const authenticityTokenInput = document.querySelector('input[name="authenticity_token"]')
    if (authenticityTokenInput && authenticityTokenInput.value) {
      return authenticityTokenInput.value
    }
    
    // Last resort: return empty string (may cause 422 error but won't crash)
    console.warn('CSRF token not found in payment methods controller. This may be due to test mode or embedded session.')
    return ''
  }

  setCardSelected(card, selected) {
    card.classList.toggle(SELECTED_CLASS, selected)
  }

  selectPaymentMethod(event) {
    const paymentMethodId = event.currentTarget.getAttribute('data-payment-method-id')
    
    // Hide new payment form if open
    const newPaymentForm = document.getElementById('new-payment-form')
    if (newPaymentForm && newPaymentForm.style.display !== 'none') {
      this.toggleNewPaymentForm()
    }
    
    // Update selected payment method
    this.selectedPaymentMethodIdValue = paymentMethodId
    
    // Update hidden input field
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = paymentMethodId
    }
    
    // Update UI to show selection
    this.updatePaymentMethodSelection()
    
    // Enable subscribe button
    this.enableSubscribeButton()
  }

  toggleNewPaymentForm() {
    const form = document.getElementById('new-payment-form')
    const card = document.getElementById('new-payment-method-card')
    
    if (!form || !card) return
    
    if (form.style.display === 'none' || form.style.display === '') {
      // Opening new payment form
      form.style.display = 'block'
      this.setCardSelected(card, true)
      this.selectedPaymentMethodIdValue = 'new'
      
      // Clear all existing payment method selections
      document.querySelectorAll('.payment-method-card:not(#new-payment-method-card)').forEach(otherCard => {
        this.setCardSelected(otherCard, false)
        
        // Remove any existing badges and add chevrons
        const badge = otherCard.querySelector('.badge')
        if (badge) {
          badge.remove()
        }
      })

      // Disable subscribe button when new payment form is open
      if (this.hasSubscribeButtonTarget) {
        this.subscribeButtonTarget.disabled = true
        this.subscribeButtonTarget.classList.add('disabled')
        this.subscribeButtonTarget.setAttribute('title', 'Veuillez remplir le formulaire de paiement')
      }
    } else {
      // Closing new payment form
      form.style.display = 'none'
      this.setCardSelected(card, false)
      this.selectedPaymentMethodIdValue = ''
      this.updatePaymentMethodSelection()
      // Update subscribe button state based on selected payment method
      this.updateSubscribeButtonState()
      return
    }
    
    // When opening the new payment form, do not call updateSubscribeButtonState() after disabling button above.
    if (form.style.display === 'none') {
      this.updateSubscribeButtonState()
    }
  }

  updatePaymentMethodSelection() {
    // Reset all cards
    document.querySelectorAll('.payment-method-card').forEach(card => {
      this.setCardSelected(card, false)
      
      const badge = card.querySelector('.badge')
      
      if (badge) {
        badge.remove()
      }
    })
    
    // Highlight selected card
    const selectedCards = document.querySelectorAll('.payment-method-card')
    selectedCards.forEach(card => {
      const paymentMethodId = card.getAttribute('data-payment-method-id')
      if (paymentMethodId && paymentMethodId === this.selectedPaymentMethodIdValue) {
        this.setCardSelected(card, true)
        
        const rightCol = card.querySelector('.col-4.text-end')
        if (rightCol) {
          rightCol.innerHTML = '<span class="badge bg-primary">Sélectionné</span>'
        }
      }
    })
  }

  enableSubscribeButton() {
    if (this.hasSubscribeButtonTarget) {
      this.subscribeButtonTarget.disabled = false
      this.subscribeButtonTarget.classList.remove('disabled')
      this.subscribeButtonTarget.removeAttribute('title')
    }
  }

  updateSubscribeButtonState() {
    if (!this.hasSubscribeButtonTarget) return
    const hasValidPaymentMethod = this.selectedPaymentMethodIdValue && 
                                 this.selectedPaymentMethodIdValue !== '' && 
                                 this.selectedPaymentMethodIdValue !== 'new' && 
                                 this.selectedPaymentMethodIdValue !== 'null'
    
    if (hasValidPaymentMethod) {
      this.enableSubscribeButton()
    } else {
      this.subscribeButtonTarget.disabled = true
      this.subscribeButtonTarget.classList.add('disabled')
      this.subscribeButtonTarget.title = 'Un moyen de paiement doit être sélectionné pour souscrire'
    }
  }

  handleSubscribe(event) {
    // Update selected payment method in hidden field before submission
    if (this.selectedPaymentMethodIdValue && this.selectedPaymentMethodIdValue !== 'new') {
      if (this.hasHiddenInputTarget) {
        this.hiddenInputTarget.value = this.selectedPaymentMethodIdValue
      }
    }
    
    // Show loading state
    this.showSubscribeLoading()
    
    // Ensure form submission happens
    const form = event.target.form
    if (form) {
      // Let natural submission happen, but fallback to forced submission if needed
      setTimeout(() => {
        form.requestSubmit(event.target)
      }, 100)
    }
  }

  showSubscribeLoading() {
    if (!this.hasSubscribeButtonTarget) return
    
    this.subscribeButtonTarget.setAttribute('data-original-value', this.subscribeButtonTarget.value)
    this.subscribeButtonTarget.disabled = true
    this.subscribeButtonTarget.value = 'Traitement en cours...'
    this.subscribeButtonTarget.classList.add('btn-loading')
    
    // Add loading animation styles
    if (!document.getElementById('subscribe-loading-style')) {
      const style = document.createElement('style')
      style.id = 'subscribe-loading-style'
      style.textContent = `
        .btn-loading {
          opacity: 0.8;
          cursor: not-allowed !important;
          animation: pulse 1.5s infinite;
        }
        @keyframes pulse {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0.8; }
        }
      `
      document.head.appendChild(style)
    }
  }

  async refreshPaymentMethodsList(paymentMethodId) {
    try {
      const response = await fetch(`/insurance_policies/${this.insurancePolicyIdValue}/payment_methods`, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-CSRF-Token': this.getCSRFToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()

      if (html.trim() === '') {
        console.error('Received empty HTML response from server')
        return
      }

      // Replace the existing payment methods list
      const existingList = document.querySelector('.existing-payment-methods')
      
      if (existingList) {
        existingList.outerHTML = html
      } else {
        // If no existing list, add it before the new payment method card
        const newPaymentCard = document.getElementById('new-payment-method-card')
        if (newPaymentCard) {
          newPaymentCard.insertAdjacentHTML('beforebegin', html)
        }
      }

      // Select the newly added payment method
      if (paymentMethodId) {
        this.selectedPaymentMethodIdValue = paymentMethodId
        
        // Update hidden input
        if (this.hasHiddenInputTarget) {
          this.hiddenInputTarget.value = paymentMethodId
        }
        
        // Update the visual selection
        setTimeout(() => {
          this.updatePaymentMethodSelection()
        }, 100)
      }

      // Hide the new payment form and reset its card style
      const newPaymentForm = document.getElementById('new-payment-form')
      const newPaymentCard = document.getElementById('new-payment-method-card')
      if (newPaymentForm) {
        newPaymentForm.style.display = 'none'
      }
      if (newPaymentCard) {
        this.setCardSelected(newPaymentCard, false)
      }

      // Reset the Stripe form for the next payment method entry
      // Make form visible temporarily so Stripe Elements can reinitialize properly
      if (newPaymentForm && newPaymentForm.style.display === 'none') {
        newPaymentForm.style.display = 'block'
        
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('stripe:resetForm'))
          
          // Hide the form again after reset completes
          setTimeout(() => {
            if (newPaymentForm) {
              newPaymentForm.style.display = 'none'
            }
          }, 200)
        }, 50)
      } else {
        // If form is already visible, just reset it
        document.dispatchEvent(new CustomEvent('stripe:resetForm'))
      }

      // Update button state
      this.updateSubscribeButtonState()

    } catch (error) {
      console.error('Error refreshing payment methods:', error)
      // Fallback: reload the page
      location.reload()
    }
  }

  setupPaymentComponent() {
    // Initialize payment method state
    if (!this.selectedPaymentMethodIdValue || this.selectedPaymentMethodIdValue === 'null' || this.selectedPaymentMethodIdValue === 'undefined') {
      this.selectedPaymentMethodIdValue = ''
    }

    // Auto-open form sets selectedPaymentMethodId to 'new'
    if (!this.hasExistingPaymentMethodsValue) {
      const newPaymentForm = document.getElementById('new-payment-form')
      if (newPaymentForm && newPaymentForm.style.display !== 'none') {
        this.selectedPaymentMethodIdValue = 'new'
      }
    }

    this.updateSubscribeButtonState()

    // Set up global function for Stripe Elements integration
    window.refreshPaymentMethodsList = (paymentMethodId) => {
      this.refreshPaymentMethodsList(paymentMethodId)
    }

    // Listen for Stripe payment method added events
    document.addEventListener('stripe:paymentMethodAdded', (event) => {
      const paymentMethodId = event.detail.paymentMethodId
      this.refreshPaymentMethodsList(paymentMethodId)
    })
  }
}
